import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { optionalEmailSchema } from '@/lib/validations'
import { UserRole } from '@magnetar/admin-api-interfaces'
import { clinicGroupUsersService } from '@/services/clinic_group_users_service'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/useAuth'
import { useViewMode } from '@/stores/viewMode'

const addUserSchema = z.object({
  account: z.string().min(1, '請輸入帳號'),
  user_name: z.string().min(1, '請輸入名稱'),
  email: optionalEmailSchema(),
  phone: z.string().optional(),
  user_role_id: z.string().optional(),
})

type AddUserValues = z.infer<typeof addUserSchema>

interface ClinicGroupUserAddDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
  availableRoles?: UserRole[]
}

export function ClinicGroupUserAddDialog({
  open,
  onOpenChange,
  onSuccess,
  availableRoles = [],
}: ClinicGroupUserAddDialogProps) {
  const { token } = useAuth()
  const { id: clinicGroupId } = useViewMode()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm<AddUserValues>({
    resolver: zodResolver(addUserSchema),
    defaultValues: {
      account: '',
      user_name: '',
      email: '',
      phone: '',
      user_role_id: '',
    },
  })

  const onSubmit = async (values: AddUserValues) => {
    if (!clinicGroupId || !token) return
    if (!values.email && !values.phone) {
      toast.error('Email 或手機至少需填寫一個')
      return
    }
    setIsSubmitting(true)
    try {
      const result = await clinicGroupUsersService.createNewUserAndAddToGroup(
        {
          clinic_group_id: clinicGroupId,
          account: values.account,
          user_name: values.user_name,
          email: values.email || undefined,
          phone: values.phone || undefined,
          user_role_id: values.user_role_id ? Number(values.user_role_id) : undefined,
        },
        token,
      )

      if (result.success) {
        toast.success('使用者已建立')
        form.reset()
        onSuccess()
        onOpenChange(false)
      } else {
        toast.error(result.error?.message || '建立使用者失敗')
      }
    } catch {
      toast.error('建立使用者失敗')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>新增集團使用者</DialogTitle>
          <DialogDescription>建立新的診所集團使用者</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="account"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>帳號</FormLabel>
                  <FormControl>
                    <Input placeholder="輸入帳號" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="user_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>名稱</FormLabel>
                  <FormControl>
                    <Input placeholder="輸入使用者名稱" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="輸入 Email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>手機</FormLabel>
                  <FormControl>
                    <Input placeholder="輸入手機號碼" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="user_role_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>角色 (選填)</FormLabel>
                  <Select
                    onValueChange={(value) => field.onChange(value === 'none' ? '' : value)}
                    value={field.value || 'none'}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="選擇角色" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">無角色</SelectItem>
                      {availableRoles.map((role) => (
                        <SelectItem key={role.user_role_id} value={role.user_role_id.toString()}>
                          {role.role_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                取消
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? '處理中...' : '新增'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
